/** Compact import-runs list embedded in another record's detail view (the
 *  Booking / Order "Import" tabs) — the same columns, chips and cell popovers
 *  as the /import-runs grid, over a caller-provided row array.
 *
 *  Clicking a row opens the shared ImportRunDetailDialog IN PLACE (local
 *  state, not the route — the host page keeps its own URL; the dialog's Share
 *  button still copies the canonical /import-runs/:id/:tab link). Cell
 *  quick-filter actions open the full /import-runs grid IN A NEW TAB with that
 *  chip applied — the host record stays where it is. Everything that needs
 *  `console_import_runs_view` is hidden when
 *  the operator lacks it — the embedded rows themselves come from the host
 *  record's endpoint (booking/order perms).
 */
import { Typography } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useCallback, useMemo, useState } from 'react';

import type { ImportRunListItem } from '@/api/importRuns';
import { useAuth } from '@/auth/AuthProvider';
import { TARGET_ROW_SX, targetRowClassName } from '@/components/DataGrid/targetRow';
import { equalityOperator } from '@/components/QuerySearch';
import { importRunsHref } from '@/utils/gridLinks';
import { openInNewTab } from '@/utils/openInNewTab';

import { ImportRunDetailDialog } from './detail/ImportRunDetailDialog';
import { buildColumns } from './importRunsColumns';
import { buildImportRunNamespaces } from './importRunNamespaces';

/** Embedded column subset — group/child_count/url add nothing under a record
 *  (runs attached to a booking/order are the concrete runs, never parents). */
const FIELDS = new Set([
  'open', 'created_ts', 'state', 'origin', 'sources', 'processing_duration', 'id', 'request_id',
]);

/** `highlightId` tints the run another surface points at (the booking's time axis). */
export function EmbeddedImportRuns({ runs, highlightId }: { runs: ImportRunListItem[]; highlightId?: string | null }) {
  const auth = useAuth();
  const namespaces = useMemo(() => buildImportRunNamespaces(), []);
  const canViewImports = auth.hasPerm('console_import_runs_view');
  const [openRun, setOpenRun] = useState<{ id: string; tab?: string } | null>(null);

  // Quick filters open the full grid (new tab) with the chip pre-applied.
  const onQuickFilter = useCallback(
    (namespace: string, value: string) => {
      if (namespace === 'tenant') {
        openInNewTab(importRunsHref([], value));
        return;
      }
      const cfg = namespaces[namespace];
      const operator = cfg ? equalityOperator(cfg.operators) : null;
      if (!operator) return;
      openInNewTab(importRunsHref([{ kind: 'cond', namespace, operator, value }]));
    },
    [namespaces],
  );

  const detailPath = useCallback((id: string) => `/import-runs/${id}/info`, []);

  const columns = useMemo(() => {
    const cols = buildColumns({ showTenant: false, onQuickFilter, detailPath }).filter((c) =>
      FIELDS.has(c.field),
    );
    // Without the imports permission the detail routes/dialog are off-limits —
    // drop the open column (the grid data itself comes from the host record).
    return canViewImports ? cols : cols.filter((c) => c.field !== 'open');
  }, [onQuickFilter, detailPath, canViewImports]);

  if (!runs.length) {
    return (
      <Typography variant="body2" color="text.disabled" sx={{ p: 2 }}>
        No import runs.
      </Typography>
    );
  }

  return (
    <>
      <DataGridPro
        rows={runs}
        columns={columns}
        getRowId={(r) => r.id}
        density="compact"
        autoHeight
        hideFooter
        disableColumnFilter
        disableColumnMenu
        disableRowSelectionOnClick
        initialState={{ sorting: { sortModel: [{ field: 'created_ts', sort: 'desc' }] } }}
        getRowClassName={targetRowClassName(highlightId)}
        sx={{ ...TARGET_ROW_SX, ...(canViewImports ? { '& .MuiDataGrid-row': { cursor: 'pointer' } } : {}) }}
        {...(canViewImports ? { onRowClick: (p: { id: string | number }) => setOpenRun({ id: String(p.id) }) } : {})}
      />
      {openRun && canViewImports && (
        <ImportRunDetailDialog
          runId={openRun.id}
          tab={openRun.tab}
          onClose={() => setOpenRun(null)}
          onNavigateTab={(t) => setOpenRun({ id: openRun.id, tab: t })}
          detailPath={detailPath}
        />
      )}
    </>
  );
}
